import React from "react";
import { Eye, EyeOff } from "lucide-react";

import { INPUT_CLASS, SUBMIT_CLASS, ERROR_CLASS } from "../Auth/authStyles";

const CATEGORIES = ["investor", "founder", "ambassador"];

// "Join as an founder" — the label used to hardcode "an".
const article = (word) => (/^[aeiou]/i.test(word) ? "an" : "a");

// One text input plus the red line the original form showed under it.
const Field = ({
  name,
  placeholder,
  error,
  onChange,
  value,
  autoComplete,
  type = "text",
}) => (
  <div>
    <input
      type={type}
      name={name}
      placeholder={placeholder}
      aria-label={placeholder}
      value={value}
      onChange={onChange}
      autoComplete={autoComplete}
      required
      className={INPUT_CLASS}
    />
    {error && <p className={ERROR_CLASS}>{error}</p>}
  </div>
);

const PasswordField = ({
  name,
  placeholder,
  error,
  onChange,
  value,
  shown,
  onToggle,
}) => (
  <div>
    <div className="relative">
      <input
        type={shown ? "text" : "password"}
        name={name}
        placeholder={placeholder}
        aria-label={placeholder}
        value={value}
        onChange={onChange}
        autoComplete="new-password"
        required
        className={`${INPUT_CLASS} pr-12`}
      />
      <button
        type="button"
        onClick={onToggle}
        aria-label={shown ? "Hide password" : "Show password"}
        className="absolute right-3 top-1/2 -translate-y-1/2 bg-transparent border-0 p-1 text-black/40 hover:text-accent-on-light transition-colors"
      >
        {shown ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
      </button>
    </div>
    {error && <p className={ERROR_CLASS}>{error}</p>}
  </div>
);

// No onClick — the parent form's onSubmit routes to the right step handler.
const SubmitButton = ({ label, loading, loadingLabel }) => (
  <button
    type="submit"
    disabled={loading}
    className={`${SUBMIT_CLASS} mt-3 disabled:opacity-60 disabled:cursor-not-allowed`}
  >
    {loading ? (
      <>
        <span className="w-4 h-4 rounded-full border-2 border-[#1b0a44]/30 border-t-[#1b0a44] animate-spin" />
        {loadingLabel}
      </>
    ) : (
      <>
        <span className="inline-block group-hover:translate-x-1 transition-transform duration-300">
          {label}
        </span>
        <span className="material-symbols-outlined text-[18px] group-hover:animate-arrow-slide">
          arrow_forward
        </span>
      </>
    )}
  </button>
);

export const ProfileStep = ({
  user,
  errors,
  imgPreview,
  onImageChange,
  onChange,
}) => (
  <>
    <div className="flex !flex-nowrap !justify-start items-center gap-4">
      <div
        className="w-16 h-16 rounded-full shrink-0 bg-black/[0.05] border border-black/10 bg-cover bg-center"
        style={imgPreview ? { backgroundImage: `url("${imgPreview}")` } : undefined}
      />

      <div>
        <input
          type="file"
          id="file"
          accept="image/png, image/jpeg, image/webp"
          className="hidden"
          onChange={onImageChange}
        />
        <label
          htmlFor="file"
          className="inline-flex items-center rounded-md border border-black/15 px-4 py-2 font-headline font-bold text-[13px] text-[#111113] cursor-pointer hover:border-accent-on-light hover:text-accent-on-light transition-colors"
        >
          Upload image
        </label>
        <p className="font-body text-[12px] text-black/40 mt-1.5 mb-0">
          PNG or JPG, square works best.
        </p>
      </div>
    </div>

    {errors.image && <p className={ERROR_CLASS}>{errors.image}</p>}

    <Field
      name="fullName"
      placeholder="Full name"
      autoComplete="name"
      value={user.fullName}
      onChange={onChange}
      error={errors.fullName}
    />

    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      <Field
        name="title"
        placeholder="Title"
        autoComplete="organization-title"
        value={user.title}
        onChange={onChange}
        error={errors.title}
      />
      <Field
        name="location"
        placeholder="Location"
        autoComplete="address-level2"
        value={user.location}
        onChange={onChange}
        error={errors.location}
      />
    </div>

    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      <Field
        name="twitter"
        placeholder="Twitter URL"
        autoComplete="url"
        value={user.twitter}
        onChange={onChange}
        error={errors.twitter}
      />
      <Field
        name="linkedIn"
        placeholder="LinkedIn URL"
        autoComplete="url"
        value={user.linkedIn}
        onChange={onChange}
        error={errors.linkedIn}
      />
    </div>

    <SubmitButton label="Continue" />
  </>
);

export const AccountStep = ({
  user,
  errors,
  formError,
  formSuccess,
  submitting,
  onChange,
  onSelectCategory,
  onBack,
  showPassword,
  onTogglePassword,
  showConfirm,
  onToggleConfirm,
}) => (
  <>
    <Field
      type="email"
      name="email"
      placeholder="you@example.com"
      autoComplete="email"
      value={user.email}
      onChange={onChange}
      error={errors.email}
    />

    <div>
      <p className="font-body text-[13px] text-[#4a4753] mt-0 mb-2">
        Which best describes you?
      </p>

      <div className="grid grid-cols-3 gap-2">
        {CATEGORIES.map((category) => (
          <label
            key={category}
            className={`flex !flex-nowrap !justify-center items-center gap-2 rounded-md border px-3 py-2.5 cursor-pointer font-body text-[13px] capitalize transition-colors ${
              user.category === category
                ? "border-accent-on-light text-accent-on-light bg-accent/[0.06]"
                : "border-black/10 text-[#4a4753] hover:border-accent-on-light/50"
            }`}
          >
            <input
              type="radio"
              name="category"
              checked={user.category === category}
              onChange={() => onSelectCategory(category)}
              className="w-4 h-4 accent-accent-on-light"
            />
            {category}
          </label>
        ))}
      </div>

      {errors.category && <p className={ERROR_CLASS}>{errors.category}</p>}
    </div>

    <Field
      name="phone"
      placeholder="Phone number"
      autoComplete="tel"
      value={user.phone}
      onChange={onChange}
      error={errors.phone}
    />

    <PasswordField
      name="password"
      placeholder="Enter your password"
      value={user.password}
      onChange={onChange}
      shown={showPassword}
      onToggle={onTogglePassword}
      error={errors.password}
    />

    <PasswordField
      name="confirmPassword"
      placeholder="Confirm password"
      value={user.confirmPassword}
      onChange={onChange}
      shown={showConfirm}
      onToggle={onToggleConfirm}
      error={errors.confirmPassword}
    />

    {formError && (
      <p
        role="alert"
        className="font-body text-[13px] leading-relaxed rounded-md border border-[#d92d20]/25 bg-[#d92d20]/[0.06] text-[#d92d20] px-4 py-3 m-0"
      >
        {formError}
      </p>
    )}

    {formSuccess && (
      <p
        role="status"
        className="font-body text-[13px] leading-relaxed rounded-md border border-accent-on-light/25 bg-accent/[0.06] text-accent-on-light px-4 py-3 m-0"
      >
        {formSuccess}
      </p>
    )}

    <SubmitButton
      label={
        user.category
          ? `Join as ${article(user.category)} ${user.category}`
          : "Join The Crypto Hub"
      }
      loading={submitting}
      loadingLabel="Submitting"
    />

    <button
      type="button"
      onClick={onBack}
      className="bg-transparent border-0 p-0 mt-1 font-body text-[13px] text-black/50 hover:text-accent-on-light cursor-pointer transition-colors"
    >
      ← Back to your details
    </button>
  </>
);
